import React from "react";
import { Link } from "react-router";

import type { ColumnDef } from "@tanstack/react-table";

import { UserAvatar } from "@/components/common/user-avatar";
import { ZoomablePhoto } from "@/components/media/photo-zoomable";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { GroupDatesContext } from "@/pages/event-groups/hooks/use-group-dates";
import type { EventGroupHit } from "@/types/event-group";

const DASH = "—";

/**
 * The member dates of one group, drawn like the events table's date cell. A one-day group shows one
 * date. Still loading shows the same blank a loading cell has, and a failure or a group with no
 * dated member shows a dash.
 */
const GroupDatesCell = ({ id }: { id: string }) => {
  const state = React.useContext(GroupDatesContext);
  if (state.status === "loading") return <div className="h-8" />;
  const range = state.status === "ready" ? state.ranges[id] : undefined;
  if (!range) return dash();
  const first = parseUnixTimestamp(
    range.first.at,
    range.first.timezone ?? undefined,
  );
  const last = parseUnixTimestamp(
    range.last.at,
    range.last.timezone ?? undefined,
  );
  const sameDay = first.format("YYYY-MM-DD") === last.format("YYYY-MM-DD");
  return makeTooltip(
    <div className="flex flex-col leading-tight">
      <span>
        {sameDay
          ? first.format("DD/MM/YYYY")
          : `${first.format("DD/MM/YYYY")} - ${last.format("DD/MM/YYYY")}`}
      </span>
    </div>,
    sameDay
      ? first.format("ddd, MMM D, YYYY HH:mm")
      : `${first.format("ddd, MMM D, YYYY")} - ${last.format("ddd, MMM D, YYYY")}`,
  );
};

const dash = () => <span className="text-muted-foreground">{DASH}</span>;

/**
 * The columns of the group list, in the order and the look of the events table: art, name, events,
 * dates, saved, created by, created at, actions. Each data column's id is the Typesense field it
 * shows, because a sort or a filter on a column sends the column id. Only the fields the collection
 * can sort on sort, and only `memberCount` takes a filter. Every column also reads its value with
 * an `accessorFn`: TanStack lets a column sort or filter only when it has one, whatever the flags
 * say, and the header menu hides both without it. The name is drawn as plain text, not as the
 * search snippet: the snippet is html.
 */
export const useEventGroupColumns = (): ColumnDef<EventGroupHit>[] => {
  const { members } = useMembers();
  return React.useMemo<ColumnDef<EventGroupHit>[]>(
    () => [
      {
        id: "photo",
        header: "Art",
        accessorFn: (row) => row.document.media?.photos?.[0],
        size: 56,
        enableSorting: false,
        enablePinning: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const photo = row.original.document.media?.photos?.[0];
          if (!photo?.url) return <></>;
          return (
            <ZoomablePhoto
              key={photo.url}
              url={photo.url}
              parentClassName="h-10 w-10"
            />
          );
        },
      },
      {
        id: "name",
        header: "Name",
        accessorFn: (row) => row.document.name,
        size: 300,
        minSize: 200,
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) =>
          makeTooltip(
            <div className="flex items-center gap-2 truncate">
              <Link
                to={`/event-groups/show/${row.original.document.id}`}
                className="truncate font-extrabold text-foreground/80 hover:underline"
              >
                {row.original.document.name}
              </Link>
              {row.original.document.hasExternalKey && (
                <Badge variant="secondary">Tina</Badge>
              )}
            </div>,
          ),
      },
      {
        id: "memberCount",
        header: "Events",
        accessorFn: (row) => row.document.memberCount,
        size: 90,
        enableSorting: true,
        meta: { filterType: "number" },
        // Null until the backend has counted the group: a dash, not a 0 that would be a claim.
        cell: ({ row }) => (
          <div className="tabular-nums">
            {row.original.document.memberCount ?? DASH}
          </div>
        ),
      },
      {
        id: "dates",
        header: "Dates",
        accessorFn: (row) => row.document.id,
        size: 230,
        enableSorting: false,
        enableColumnFilter: false,
        // First to last member date, from the one members query of the page (`useGroupDates`).
        cell: ({ row }) => <GroupDatesCell id={row.original.document.id} />,
      },
      {
        id: "interestedIn",
        header: "Saved",
        accessorFn: (row) => row.document.interestedIn,
        size: 120,
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="tabular-nums">
            {row.original.document.interestedIn ?? DASH}
          </div>
        ),
      },
      {
        id: "createdBy",
        header: "Created By",
        minSize: 170,
        accessorFn: (row) => row.document.createdBy,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ getValue, row }) => {
          const value = getValue() as string | null | undefined;
          // The explore document leaves the creator out; a group Tina made says so, as the name column does.
          if (!value)
            return row.original.document.hasExternalKey ? (
              <Badge variant="secondary">Tina</Badge>
            ) : (
              dash()
            );
          const member = members.find(
            (x) =>
              x.id === value || x.adminMetadata?.relatedIds?.includes(value),
          );
          return member ? (
            <div className="p-1">
              <UserAvatar {...member} half />
            </div>
          ) : (
            value
          );
        },
      },
      {
        id: "createdAt",
        header: "Created At",
        accessorFn: (row) => row.document.createdAt,
        enableSorting: false,
        enableColumnFilter: false,
        // Unix seconds, not an ISO string.
        cell: ({ row }) => {
          const { createdAt } = row.original.document;
          if (createdAt == null) return dash();
          const created = parseUnixTimestamp(createdAt);
          return makeTooltip(
            created.format("DD/MM/YYYY HH:mm"),
            created.format("ddd, MMM D, YYYY HH:mm"),
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 140,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <>
            <EditButton
              size="sm"
              resource="event-groups"
              recordItemId={row.original.document.id}
              hideText
              newTab
            />
            <ShowButton
              size="sm"
              resource="event-groups"
              recordItemId={row.original.document.id}
              hideText
            />
          </>
        ),
      },
    ],
    [members],
  );
};
